import React, { useEffect, useRef, useState } from "react";
import { getInstance } from "@harbor/anchor/runtime";
import PropTypes from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import Spinner from "../../common/Spinner";
import MFEUnavailableState from "./MFEUnavailableState";
import {
  getMFERuntimeStatus,
  MFE_RUNTIME_EVENTS,
  MFE_RUNTIME_STATUS,
} from "./MFERuntimeStatus";

const [HbrCard] = reactWrapper(["hbr-card"]);

/**
 * Component that renders a generic view from Harbor Anchor
 */
const GenericViewRenderer = ({ genericView }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!genericView || !containerRef.current) return;

    // Get the DOM node from the generic view
    const node = genericView.getDOMNode();

    // Clear the container and append the node
    containerRef.current.innerHTML = "";
    containerRef.current.append(node);

    // Call onRender and setup cleanup
    genericView.onRender();
    return () => {
      if (genericView.dispose) {
        genericView.dispose();
      }
    };
  }, [genericView]);

  return <div ref={containerRef} />;
};

GenericViewRenderer.propTypes = {
  genericView: PropTypes.shape({
    getDOMNode: PropTypes.func.isRequired,
    onRender: PropTypes.func.isRequired,
    dispose: PropTypes.func,
  }),
};

/**
 * Microfrontend Widget component that loads components using Harbor Anchor
 *
 * Callers receive the shared unavailable experience by default. A caller can
 * provide errorComponent for static UI or renderError for component-aware UI.
 * renderError receives { componentId, error, isCard }.
 */
const UnifyMFEWidgetRenderer = ({
  componentId = "cisco.sdwan.app.header/aiAssistant", //default componentId
  fallbackComponent = <hbr-spinner size="small" />, // Default fallback
  errorComponent,
  renderError,
  isCard,
}) => {
  const [anchorComponent, setAnchorComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const showUnavailable = (reason = "runtime-unavailable") => {
      if (!isMounted) return;
      setAnchorComponent(null);
      setError(reason);
      setLoading(false);
    };

    const loadComponent = async () => {
      try {
        const anchor = getInstance();
        if (!anchor) return;

        setLoading(true);
        setError(null);
        setAnchorComponent(null);

        // Import the component from the Anchor registry
        const genericView = await anchor.import(componentId);

        if (!genericView) {
          throw new Error(`Failed to load component: ${componentId}`);
        }

        if (isMounted) {
          setAnchorComponent(genericView);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error loading microfrontend component:", error);
        showUnavailable("component-import-failed");
      }
    };

    const handleRuntimeFailure = () =>
      showUnavailable("runtime-initialization-failed");

    window.addEventListener(MFE_RUNTIME_EVENTS.INITIALIZED, loadComponent);
    window.addEventListener(MFE_RUNTIME_EVENTS.FAILED, handleRuntimeFailure);

    if (getMFERuntimeStatus() === MFE_RUNTIME_STATUS.FAILED) {
      handleRuntimeFailure();
    } else if (getInstance()) {
      loadComponent();
    }

    return () => {
      isMounted = false;
      window.removeEventListener(MFE_RUNTIME_EVENTS.INITIALIZED, loadComponent);
      window.removeEventListener(MFE_RUNTIME_EVENTS.FAILED, handleRuntimeFailure);
    };
  }, [componentId]);

  const unavailableComponent = renderError
    ? renderError({ componentId, error, isCard })
    : errorComponent || (
      <MFEUnavailableState
        componentId={componentId}
        display={isCard ? "full" : "inline"}
      />
    );

  if (loading) {
    return isCard ? (
      <HbrCard className="widget-container" container>
        <Spinner />
      </HbrCard>
    ) : (
      fallbackComponent
    );
  }

  if (!anchorComponent || error) {
    return isCard ? (
      <HbrCard className="widget-container" container>
        {unavailableComponent}
      </HbrCard>
    ) : (
      unavailableComponent
    );
  }

  return <GenericViewRenderer genericView={anchorComponent} />;
};

UnifyMFEWidgetRenderer.propTypes = {
  componentId: PropTypes.string,
  fallbackComponent: PropTypes.node,
  errorComponent: PropTypes.node,
  renderError: PropTypes.func,
  isCard: PropTypes.bool,
};

export default UnifyMFEWidgetRenderer;
